import React from 'react'

import { LocaleLink } from '@/components/LocaleLink'
import { normalizeHex } from '@/fields/corHex'
import { cn } from '@/utilities/ui'

import { SetaPontos } from './SetaPontos'
import { botaoValido, resolverHrefBotao, type BotaoLike } from './resolverHrefBotao'

type Props = {
  botao: BotaoLike | null | undefined
  className?: string
  /** Largura total no móvel (hero). */
  blocoNoMovel?: boolean
  tamanho?: 'normal' | 'pequeno'
}

const BASE =
  'ag-btn ag-mono group inline-flex items-center justify-between gap-2 lg:gap-4 text-[12px] lg:text-[14px] transition-all duration-300 cursor-pointer whitespace-nowrap focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ag-primary)]'

const TAMANHO = {
  normal: 'h-[40px] px-4 lg:h-[54px] lg:px-[28px]',
  pequeno: 'h-[40px] px-4 lg:h-[46px] lg:px-5',
}

export const VARIANTES: Record<string, string> = {
  primario:
    'bg-[var(--ag-primary)] text-[var(--ag-text-light)] hover:bg-[var(--ag-primary-hover)]',
  secundario:
    'bg-[var(--ag-bg-light)] text-[var(--ag-text-dark)] border border-[rgb(var(--ag-text-dark-rgb)/0.2)] hover:bg-[rgb(var(--ag-bg-light-rgb)/0.8)]',
  contorno:
    'bg-transparent text-[var(--sec-fg)] border border-[rgb(var(--sec-border-rgb)/0.5)] hover:bg-[rgb(var(--sec-fg-rgb)/0.06)]',
  escuro: 'bg-[var(--ag-bg-dark)] text-[var(--ag-text-light)] hover:opacity-85',
  link: 'h-auto px-0 lg:h-auto lg:px-0 text-[var(--sec-fg)] hover:opacity-70 justify-start',
}

/** Botão do sistema de agência (rótulo mono + ponteiro de três pontos que desliza no hover). */
export function BotaoAgencia({ botao, className, blocoNoMovel, tamanho = 'normal' }: Props) {
  if (!botaoValido(botao)) return null
  const b = botao!
  const href = resolverHrefBotao(b)!
  const variante = b.variante && b.variante in VARIANTES ? b.variante : 'primario'
  const style: React.CSSProperties = {}
  const bg = normalizeHex(b.corFundo)
  const fg = normalizeHex(b.corTexto)
  if (bg) style.backgroundColor = bg
  if (fg) style.color = fg
  const ext = /^https?:\/\//i.test(href) || /^mailto:|^tel:/i.test(href)
  const commonProps = {
    className: cn(
      BASE,
      variante !== 'link' && TAMANHO[tamanho],
      VARIANTES[variante],
      blocoNoMovel && 'w-full lg:w-auto',
      className,
    ),
    style,
    ...(b.novaAba || ext ? { target: '_blank', rel: 'noopener noreferrer' } : {}),
  }
  const inner = (
    <>
      <span>{b.rotulo}</span>
      {b.mostrarSeta !== false ? (
        <span className="flex items-center transition-transform duration-300 group-hover:translate-x-1">
          <SetaPontos size={variante === 'link' ? 16 : 18} />
        </span>
      ) : null}
    </>
  )
  if (ext) {
    return (
      <a href={href} {...commonProps}>
        {inner}
      </a>
    )
  }
  return (
    <LocaleLink href={href} {...commonProps}>
      {inner}
    </LocaleLink>
  )
}
